/**
 * ASSETS VECTORIALES Y PRECARGADOR DE IMÁGENES PARA CARRERA CONTINUA (HU-06)
 * Proporciona recursos vectoriales Full HD de alta calidad y utilidad de caché en memoria.
 */

/**
 * Resuelve una URL de asset relativa (ej. /uploads/carrera-continua/xxx.webp)
 * anteponiendo el origen del backend si no es una URL absoluta o Data URI.
 */
export const resolveAssetUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    const clean = url.trim();
    if (!clean) return '';
    if (clean.startsWith('http://') || clean.startsWith('https://') || clean.startsWith('data:') || clean.startsWith('blob:')) {
        return clean;
    }
    const apiBase = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const backendOrigin = apiBase.replace(/\/api\/?$/i, '');
    const normalizedPath = clean.startsWith('/') ? clean : `/${clean}`;
    return `${backendOrigin}${normalizedPath}`;
};

// Precargador de imágenes en memoria del navegador
export const preloadImageUrls = (urls = []) => {
    if (!Array.isArray(urls)) return Promise.resolve();
    const cleanUrls = urls
        .filter(u => typeof u === 'string' && u.trim().length > 0)
        .map(u => resolveAssetUrl(u));
    const promises = cleanUrls.map(url => {
        return new Promise((resolve) => {
            const img = new Image();
            img.src = url;
            img.onload = () => resolve({ url, success: true });
            img.onerror = () => resolve({ url, success: false });
        });
    });
    return Promise.all(promises);
};

// SVG Data URIs por defecto para Parallax 1920x1080 (Sky, City, Track)
export const DEFAULT_SKY_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="1920" height="1080" viewBox="0 0 1920 1080">
  <defs>
    <linearGradient id="skyGrad" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="%230f2b48" />
      <stop offset="40%" stop-color="%231a4a75" />
      <stop offset="70%" stop-color="%232e73a8" />
      <stop offset="100%" stop-color="%2368a5cf" />
    </linearGradient>
    <radialGradient id="sunGlow" cx="80%" cy="20%" r="40%">
      <stop offset="0%" stop-color="%23fff5cc" stop-opacity="0.9" />
      <stop offset="30%" stop-color="%23ffbb33" stop-opacity="0.4" />
      <stop offset="70%" stop-color="%23ff8800" stop-opacity="0.1" />
      <stop offset="100%" stop-color="%231a4a75" stop-opacity="0" />
    </radialGradient>
  </defs>
  <rect width="1920" height="1080" fill="url(%23skyGrad)" />
  <circle cx="1500" cy="180" r="160" fill="url(%23sunGlow)" />
  <!-- Nubes estilizadas -->
  <g fill="%23ffffff" opacity="0.25">
    <ellipse cx="280" cy="140" rx="140" ry="40" />
    <ellipse cx="360" cy="120" rx="90" ry="50" />
    <ellipse cx="850" cy="200" rx="180" ry="45" />
    <ellipse cx="940" cy="170" rx="110" ry="55" />
    <ellipse cx="1400" cy="110" rx="160" ry="38" />
    <ellipse cx="1780" cy="230" rx="130" ry="42" />
  </g>
</svg>`;

export const DEFAULT_CITY_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="1920" height="600" viewBox="0 0 1920 600">
  <defs>
    <linearGradient id="cityGradFar" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="%231b314b" stop-opacity="0.7" />
      <stop offset="100%" stop-color="%230c1724" stop-opacity="0.95" />
    </linearGradient>
    <linearGradient id="cityGradNear" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="%23244265" />
      <stop offset="100%" stop-color="%230d1a29" />
    </linearGradient>
    <linearGradient id="beamGrad" x1="0%" y1="100%" x2="50%" y2="0%">
      <stop offset="0%" stop-color="%23ffff99" stop-opacity="0.4" />
      <stop offset="100%" stop-color="%23ffff99" stop-opacity="0" />
    </linearGradient>
  </defs>
  <!-- Reflectores de Estadio -->
  <polygon points="180,450 120,0 260,0" fill="url(%23beamGrad)" />
  <polygon points="820,450 760,0 900,0" fill="url(%23beamGrad)" />
  <polygon points="1460,450 1400,0 1540,0" fill="url(%23beamGrad)" />
  <!-- Silueta Lejana -->
  <g fill="url(%23cityGradFar)">
    <rect x="40" y="240" width="90" height="360" rx="4" />
    <rect x="150" y="180" width="120" height="420" rx="4" />
    <rect x="290" y="270" width="80" height="330" rx="4" />
    <rect x="400" y="140" width="140" height="460" rx="4" />
    <polygon points="470,80 430,140 510,140" />
    <rect x="560" y="210" width="110" height="390" rx="4" />
    <rect x="690" y="280" width="90" height="320" rx="4" />
    <rect x="800" y="160" width="160" height="440" rx="4" />
    <polygon points="880,100 840,160 920,160" />
    <rect x="980" y="230" width="100" height="370" rx="4" />
    <rect x="1100" y="190" width="130" height="410" rx="4" />
    <rect x="1250" y="260" width="90" height="340" rx="4" />
    <rect x="1360" y="150" width="150" height="450" rx="4" />
    <polygon points="1435,90 1395,150 1475,150" />
    <rect x="1530" y="220" width="110" height="380" rx="4" />
    <rect x="1660" y="170" width="130" height="430" rx="4" />
    <rect x="1810" y="250" width="90" height="350" rx="4" />
  </g>
  <!-- Gradería y Silueta Cercana -->
  <g fill="url(%23cityGradNear)">
    <rect x="0" y="380" width="1920" height="220" />
    <!-- Luces de ventanas -->
    <g fill="%23ffd700" opacity="0.6">
      <rect x="170" y="210" width="10" height="15" />
      <rect x="190" y="210" width="10" height="15" />
      <rect x="170" y="240" width="10" height="15" />
      <rect x="420" y="180" width="12" height="18" />
      <rect x="445" y="180" width="12" height="18" />
      <rect x="420" y="220" width="12" height="18" />
      <rect x="445" y="220" width="12" height="18" />
      <rect x="830" y="200" width="14" height="20" />
      <rect x="860" y="200" width="14" height="20" />
      <rect x="1390" y="190" width="12" height="18" />
      <rect x="1420" y="190" width="12" height="18" />
      <rect x="1690" y="210" width="10" height="15" />
      <rect x="1720" y="210" width="10" height="15" />
    </g>
  </g>
  <!-- Torres de iluminación del estadio -->
  <g stroke="%236688aa" stroke-width="4" fill="none">
    <line x1="180" y1="450" x2="180" y2="280" />
    <line x1="160" y1="280" x2="200" y2="280" />
    <line x1="820" y1="450" x2="820" y2="280" />
    <line x1="800" y1="280" x2="840" y2="280" />
    <line x1="1460" y1="450" x2="1460" y2="280" />
    <line x1="1440" y1="280" x2="1480" y2="280" />
  </g>
</svg>`;

export const DEFAULT_TRACK_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="1920" height="480" viewBox="0 0 1920 480">
  <defs>
    <linearGradient id="tartanGrad" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="%23b91c1c" />
      <stop offset="25%" stop-color="%23991b1b" />
      <stop offset="60%" stop-color="%237f1d1d" />
      <stop offset="100%" stop-color="%23581313" />
    </linearGradient>
    <linearGradient id="grassGrad" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="%2315803d" />
      <stop offset="100%" stop-color="%23166534" />
    </linearGradient>
  </defs>
  <!-- Franja de césped superior -->
  <rect x="0" y="0" width="1920" height="30" fill="url(%23grassGrad)" />
  <line x1="0" y1="30" x2="1920" y2="30" stroke="%23ffffff" stroke-width="4" />
  <!-- Pista de atletismo tartán -->
  <rect x="0" y="32" width="1920" height="418" fill="url(%23tartanGrad)" />
  <!-- Líneas divisorias de carriles -->
  <line x1="0" y1="130" x2="1920" y2="130" stroke="%23ffffff" stroke-width="4" stroke-dasharray="40 25" opacity="0.85" />
  <line x1="0" y1="230" x2="1920" y2="230" stroke="%23ffffff" stroke-width="4" stroke-dasharray="40 25" opacity="0.85" />
  <line x1="0" y1="330" x2="1920" y2="330" stroke="%23ffffff" stroke-width="4" stroke-dasharray="40 25" opacity="0.85" />
  <line x1="0" y1="430" x2="1920" y2="430" stroke="%23ffffff" stroke-width="4" opacity="0.9" />
  <!-- Franja inferior de césped / acotamiento -->
  <rect x="0" y="450" width="1920" height="30" fill="url(%23grassGrad)" />
</svg>`;

export const DEFAULT_COVER_BG_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="1920" height="1080" viewBox="0 0 1920 1080">
  <defs>
    <radialGradient id="coverBg" cx="50%" cy="40%" r="70%">
      <stop offset="0%" stop-color="%231e3a8a" />
      <stop offset="45%" stop-color="%230f172a" />
      <stop offset="100%" stop-color="%23020617" />
    </radialGradient>
    <linearGradient id="redStripe" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="%23ef4444" stop-opacity="0.8" />
      <stop offset="100%" stop-color="%23dc2626" stop-opacity="0.1" />
    </linearGradient>
    <linearGradient id="goldStripe" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="%23f59e0b" stop-opacity="0.8" />
      <stop offset="100%" stop-color="%23d97706" stop-opacity="0.1" />
    </linearGradient>
  </defs>
  <rect width="1920" height="1080" fill="url(%23coverBg)" />
  <!-- Rayos y diagonales de velocidad deportiva -->
  <polygon points="0,0 700,0 350,1080 0,1080" fill="url(%23redStripe)" />
  <polygon points="1220,0 1920,0 1920,1080 1570,1080" fill="url(%23goldStripe)" />
  <circle cx="960" cy="450" r="450" fill="%233b82f6" opacity="0.08" />
</svg>`;

/**
 * Genera un SVG de avatar corredor en vector con color de camiseta dinámico
 */
export const getDynamicRunnerSvg = (jerseyColor = '#dc2626', zoneName = '1') => {
    const encodedColor = encodeURIComponent(jerseyColor);
    return `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="160" height="200" viewBox="0 0 160 200">
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="4" stdDeviation="6" flood-color="%23000000" flood-opacity="0.6"/>
        </filter>
        <linearGradient id="jersey" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="${encodedColor}"/>
          <stop offset="100%" stop-color="%23111111"/>
        </linearGradient>
      </defs>
      <g filter="url(%23shadow)">
        <!-- Cabeza con cinta de corredor -->
        <circle cx="80" cy="38" r="22" fill="%23ffdbac"/>
        <path d="M 60 30 Q 80 18 100 30" stroke="${encodedColor}" stroke-width="8" stroke-linecap="round" fill="none"/>
        <!-- Pelo -->
        <path d="M 58 35 Q 80 14 102 35" fill="%232d3748"/>
        <!-- Cara alegre/competitiva -->
        <circle cx="74" cy="38" r="3" fill="%231a202c"/>
        <circle cx="86" cy="38" r="3" fill="%231a202c"/>
        <path d="M 74 46 Q 80 52 86 46" stroke="%23c53030" stroke-width="2.5" fill="none"/>
        <!-- Torso con Camiseta -->
        <path d="M 60 62 L 100 62 L 95 125 L 65 125 Z" fill="url(%23jersey)" rx="6"/>
        <!-- Número de dorsal -->
        <circle cx="80" cy="90" r="14" fill="%23ffffff"/>
        <text x="80" y="96" font-size="16" font-family="Montserrat, sans-serif" font-weight="900" fill="%231a202c" text-anchor="middle">${encodeURIComponent(String(zoneName).slice(0, 3))}</text>
        <!-- Brazos en postura de sprint -->
        <!-- Brazo izquierdo (adelante) -->
        <path d="M 64 70 L 40 92 L 25 80" stroke="%23ffdbac" stroke-width="12" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
        <circle cx="25" cy="80" r="7" fill="%23ffdbac"/>
        <!-- Brazo derecho (atrás) -->
        <path d="M 96 70 L 122 90 L 138 108" stroke="%23ffdbac" stroke-width="12" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
        <circle cx="138" cy="108" r="7" fill="%23ffdbac"/>
        <!-- Shorts deportivos -->
        <path d="M 65 120 L 95 120 L 98 145 L 82 145 L 80 135 L 78 145 L 62 145 Z" fill="%231e293b"/>
        <!-- Pierna izquierda (adelante en zancada) -->
        <path d="M 70 145 L 50 170 L 72 195" stroke="%23ffdbac" stroke-width="13" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
        <!-- Tenis izquierdo -->
        <path d="M 66 195 L 90 195 Q 92 188 84 188 L 68 188 Z" fill="%23ffffff" stroke="${encodedColor}" stroke-width="3"/>
        <!-- Pierna derecha (atrás impulsando) -->
        <path d="M 90 145 L 120 165 L 140 180" stroke="%23ffdbac" stroke-width="13" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
        <!-- Tenis derecho -->
        <path d="M 136 180 L 158 172 Q 160 166 150 168 L 138 174 Z" fill="%23ffffff" stroke="${encodedColor}" stroke-width="3"/>
      </g>
    </svg>`;
};

/**
 * Genera un SVG de avatar victorioso alzando copa
 */
export const getDynamicWinnerSvg = (jerseyColor = '#f59e0b', zoneName = '1') => {
    const encodedColor = encodeURIComponent(jerseyColor);
    return `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="180" height="220" viewBox="0 0 180 220">
      <defs>
        <filter id="winGlow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="0" stdDeviation="12" flood-color="%23facc15" flood-opacity="0.85"/>
        </filter>
        <linearGradient id="trophyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="%23fff59d"/>
          <stop offset="50%" stop-color="%23f59e0b"/>
          <stop offset="100%" stop-color="%23b45309"/>
        </linearGradient>
      </defs>
      <!-- Copa de Campeón elevada -->
      <g transform="translate(60, 4) scale(0.65)" filter="url(%23winGlow)">
        <path d="M 10 10 L 90 10 Q 90 60 50 70 Q 10 60 10 10 Z" fill="url(%23trophyGrad)"/>
        <!-- Asas de la copa -->
        <path d="M 12 18 C -15 20 -15 50 15 52" stroke="%23f59e0b" stroke-width="8" fill="none" stroke-linecap="round"/>
        <path d="M 88 18 C 115 20 115 50 85 52" stroke="%23f59e0b" stroke-width="8" fill="none" stroke-linecap="round"/>
        <!-- Base de la copa -->
        <rect x="42" y="70" width="16" height="25" fill="url(%23trophyGrad)"/>
        <rect x="30" y="95" width="40" height="15" rx="3" fill="%2378350f"/>
        <text x="50" y="44" font-size="28" font-weight="900" fill="%23ffffff" text-anchor="middle">★</text>
      </g>
      <!-- Corredor en Celebración (Brazos Arriba) -->
      <g transform="translate(10, 40)">
        <circle cx="80" cy="40" r="20" fill="%23ffdbac"/>
        <path d="M 62 32 Q 80 20 98 32" stroke="${encodedColor}" stroke-width="7" stroke-linecap="round" fill="none"/>
        <path d="M 60 36 Q 80 18 100 36" fill="%232d3748"/>
        <!-- Ojos felices guiñando / boca en júbilo -->
        <path d="M 72 38 Q 76 34 80 38" stroke="%231a202c" stroke-width="2.5" fill="none"/>
        <path d="M 82 38 Q 86 34 90 38" stroke="%231a202c" stroke-width="2.5" fill="none"/>
        <path d="M 74 46 Q 80 56 86 46 Z" fill="%23e11d48"/>
        <!-- Torso -->
        <path d="M 62 62 L 98 62 L 94 118 L 66 118 Z" fill="${encodedColor}" rx="5"/>
        <circle cx="80" cy="86" r="12" fill="%23ffffff"/>
        <text x="80" y="91" font-size="14" font-weight="900" fill="%231a202c" text-anchor="middle">${encodeURIComponent(String(zoneName).slice(0, 3))}</text>
        <!-- Brazos en V de Victoria -->
        <path d="M 65 68 L 40 40 L 48 15" stroke="%23ffdbac" stroke-width="11" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
        <circle cx="48" cy="15" r="7" fill="%23ffdbac"/>
        <path d="M 95 68 L 120 40 L 112 15" stroke="%23ffdbac" stroke-width="11" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
        <circle cx="112" cy="15" r="7" fill="%23ffdbac"/>
        <!-- Shorts -->
        <path d="M 66 115 L 94 115 L 96 138 L 82 138 L 80 130 L 78 138 L 64 138 Z" fill="%231e293b"/>
        <!-- Piernas firmes -->
        <path d="M 70 138 L 70 170" stroke="%23ffdbac" stroke-width="12" stroke-linecap="round" fill="none"/>
        <rect x="62" y="168" width="22" height="10" rx="3" fill="%23ffffff" stroke="${encodedColor}" stroke-width="2.5"/>
        <path d="M 90 138 L 90 170" stroke="%23ffdbac" stroke-width="12" stroke-linecap="round" fill="none"/>
        <rect x="82" y="168" width="22" height="10" rx="3" fill="%23ffffff" stroke="${encodedColor}" stroke-width="2.5"/>
      </g>
    </svg>`;
};

/**
 * Genera un SVG de arco inflable de kilometraje con espuma deportiva optimizado para corredores de 200px
 */
export const getInflatableArchSvg = (labelText = 'KM 1', foamColorHex = '#00e5ff', sponsorLogoUrl = '', isFinish = false) => {
    const encodedFoam = encodeURIComponent(foamColorHex);
    const archColor = isFinish ? '%23f59e0b' : '%232563eb';
    const topBannerBg = isFinish ? '%23dc2626' : '%231e293b';

    return `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="460" height="520" viewBox="0 0 460 520">
      <defs>
        <filter id="archShadow" x="-20%" y="-10%" width="140%" height="130%">
          <feDropShadow dx="0" dy="8" stdDeviation="10" flood-color="%23000000" flood-opacity="0.75"/>
        </filter>
        <linearGradient id="columnGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stop-color="%23ffffff" stop-opacity="0.3"/>
          <stop offset="50%" stop-color="${archColor}"/>
          <stop offset="100%" stop-color="%23000000" stop-opacity="0.4"/>
        </linearGradient>
      </defs>
      <g filter="url(%23archShadow)">
        <!-- Columna Izquierda Inflable -->
        <rect x="25" y="80" width="65" height="420" rx="32" fill="url(%23columnGrad)"/>
        <!-- Columna Derecha Inflable -->
        <rect x="370" y="80" width="65" height="420" rx="32" fill="url(%23columnGrad)"/>
        <!-- Arco / Viga Superior Inflable -->
        <rect x="15" y="20" width="430" height="90" rx="45" fill="url(%23columnGrad)"/>
        <!-- Cartel Central / Banner -->
        <rect x="65" y="32" width="330" height="66" rx="10" fill="${topBannerBg}" stroke="%23ffffff" stroke-width="2"/>
        <text x="230" y="75" font-size="28" font-family="Montserrat, sans-serif" font-weight="900" fill="%23ffffff" text-anchor="middle" letter-spacing="2">${encodeURIComponent(labelText)}</text>
        <!-- Boquillas de Cañón de Espuma en las esquinas -->
        <circle cx="55" cy="65" r="16" fill="${encodedFoam}" stroke="%23ffffff" stroke-width="3.5"/>
        <circle cx="405" cy="65" r="16" fill="${encodedFoam}" stroke="%23ffffff" stroke-width="3.5"/>
        <!-- Línea de llegada a cuadros si es meta -->
        ${isFinish ? `<g fill="%23ffffff" stroke="%23000000" stroke-width="1">
          <rect x="40" y="90" width="26" height="18" fill="%23000000"/>
          <rect x="66" y="90" width="26" height="18" fill="%23ffffff"/>
          <rect x="92" y="90" width="26" height="18" fill="%23000000"/>
          <rect x="118" y="90" width="26" height="18" fill="%23ffffff"/>
          <rect x="144" y="90" width="26" height="18" fill="%23000000"/>
          <rect x="170" y="90" width="26" height="18" fill="%23ffffff"/>
          <rect x="196" y="90" width="26" height="18" fill="%23000000"/>
          <rect x="222" y="90" width="26" height="18" fill="%23ffffff"/>
          <rect x="248" y="90" width="26" height="18" fill="%23000000"/>
          <rect x="274" y="90" width="26" height="18" fill="%23ffffff"/>
          <rect x="300" y="90" width="26" height="18" fill="%23000000"/>
          <rect x="326" y="90" width="26" height="18" fill="%23ffffff"/>
          <rect x="352" y="90" width="26" height="18" fill="%23000000"/>
          <rect x="378" y="90" width="26" height="18" fill="%23ffffff"/>
          <rect x="404" y="90" width="26" height="18" fill="%23000000"/>
        </g>` : ''}
      </g>
    </svg>`;
};

// SVG por defecto para el fondo del aficionado en móvil (1080x1920)
export const DEFAULT_FAN_BG_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="1080" height="1920" viewBox="0 0 1080 1920">
  <defs>
    <radialGradient id="fanBgGrad" cx="50%" cy="30%" r="70%">
      <stop offset="0%" stop-color="%231e3a8a"/>
      <stop offset="50%" stop-color="%230f172a"/>
      <stop offset="100%" stop-color="%23020617"/>
    </radialGradient>
    <linearGradient id="fanTrackStripe" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="%23dc2626" stop-opacity="0.3"/>
      <stop offset="100%" stop-color="%23f59e0b" stop-opacity="0.05"/>
    </linearGradient>
  </defs>
  <rect width="1080" height="1920" fill="url(%23fanBgGrad)"/>
  <polygon points="0,0 540,0 270,1920 0,1920" fill="url(%23fanTrackStripe)"/>
  <polygon points="720,0 1080,0 1080,1920 810,1920" fill="url(%23fanTrackStripe)"/>
  <circle cx="540" cy="960" r="420" fill="%233b82f6" opacity="0.04"/>
</svg>`;

// SVG dinámico por defecto para el botón de tap del aficionado
export const DEFAULT_FAN_TAP_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="260" height="260" viewBox="0 0 260 260">
  <defs>
    <filter id="btnGlow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="8" stdDeviation="12" flood-color="%23dc2626" flood-opacity="0.7"/>
    </filter>
    <radialGradient id="btnGrad" cx="40%" cy="35%" r="65%">
      <stop offset="0%" stop-color="%23ff4d4d"/>
      <stop offset="60%" stop-color="%23dc2626"/>
      <stop offset="100%" stop-color="%23991b1b"/>
    </radialGradient>
    <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="%23ffffff"/>
      <stop offset="100%" stop-color="%23facc15"/>
    </linearGradient>
  </defs>
  <g filter="url(%23btnGlow)">
    <!-- Aro exterior brillante -->
    <circle cx="130" cy="130" r="120" fill="none" stroke="url(%23ringGrad)" stroke-width="6"/>
    <circle cx="130" cy="130" r="110" fill="url(%23btnGrad)"/>
    <!-- Relieve interior -->
    <circle cx="130" cy="130" r="95" fill="none" stroke="%23ffffff" stroke-width="2" opacity="0.4"/>
    <!-- Ícono de Tenis / Rayo de Velocidad -->
    <path d="M 85 145 C 80 135 90 115 110 105 C 130 95 155 100 170 120 C 180 132 185 142 175 155 C 160 170 120 170 95 160 Z" fill="%23ffffff"/>
    <path d="M 120 85 L 145 125 L 130 130 L 155 175 L 115 130 L 130 125 Z" fill="%23facc15" opacity="0.9"/>
    <!-- Texto ¡TAP! -->
    <text x="130" y="205" font-size="22" font-family="Montserrat, sans-serif" font-weight="900" fill="%23ffffff" text-anchor="middle" letter-spacing="3">¡CORRE!</text>
  </g>
</svg>`;
